// Port of ios/Sources/ViewModels/CustomStatus.swift.
import { civil } from './time';

/**
 * The user's custom status — the emoji + sentence Mattermost shows beside a name — plus WHEN it
 * stops being true. `expiresAt == null` means "until cleared by hand". Epoch milliseconds.
 *
 * Mirror of Android's `viewmodels/CustomStatus.kt`; the two must stay identical in behaviour
 * (CLAUDE.md's lockstep-platforms rule).
 */
export interface CustomStatus {
  emoji: string;
  text: string;
  expiresAt: number | null;
}

// MARK: - CustomStatusCodec
//
// The wire shape (`props.customStatus`, a JSON string on the user object) and the rule for
// whether a stored status still applies. Pure, because an off-by-a-zone here shows a colleague
// as "in a meeting" a full day after it ended.

/** Mattermost's "no expiry" sentinel — year one, not epoch zero. */
const noExpiryPrefix = '0001-';

export function fromPropsJSON(raw: string | null | undefined): CustomStatus | null {
  if (!raw) return null;
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof json !== 'object' || json === null || Array.isArray(json)) return null;
  const obj = json as Record<string, unknown>;
  const emoji = typeof obj.emoji === 'string' ? obj.emoji : '';
  const text = typeof obj.text === 'string' ? obj.text : '';
  if (emoji === '' && text === '') return null;
  const expiresRaw = typeof obj.expires_at === 'string' ? obj.expires_at : '';
  const expires = expiresRaw === '' || expiresRaw.startsWith(noExpiryPrefix) ? null : parseISO(expiresRaw);
  return { emoji, text, expiresAt: expires };
}

/** An expired status is NOT a status: the server clears it lazily, this client immediately. */
export function isActive(status: CustomStatus, now: number): boolean {
  if (status.expiresAt === null) return true;
  return status.expiresAt > now;
}

const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?(Z|[+-]\d{2}:?\d{2})$/;

/**
 * Mattermost sends fractional seconds on some paths and not others, and `ISO8601DateFormatter`
 * refuses the form it was not configured for — so both are tried rather than assuming one.
 * A timestamp this client cannot read must read as "no expiry", never as "expired now".
 *
 * WEB: one strict RFC 3339 pattern accepts both forms (`Date.parse` is too lenient: it would
 * read "2026-08-30" or locale-ish strings the Apple client rejects).
 */
export function parseISO(raw: string): number | null {
  const m = ISO_RE.exec(raw);
  if (!m) return null;
  const [, y, mo, d, h, mi, s, frac, zone] = m;
  const year = Number(y);
  const month = Number(mo);
  const day = Number(d);
  const hour = Number(h);
  const minute = Number(mi);
  const second = Number(s);
  if (month < 1 || month > 12 || day < 1 || day > 31 || hour > 23 || minute > 59 || second > 59) return null;
  const date = new Date(0);
  date.setUTCFullYear(year, month - 1, day);
  date.setUTCHours(hour, minute, second, frac ? Math.floor(Number('0.' + frac) * 1000) : 0);
  // Reject a day the month does not have (2026-02-30) rather than rolling it over.
  if (date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
  let offsetMinutes = 0;
  if (zone !== 'Z') {
    const sign = zone[0] === '-' ? -1 : 1;
    const digits = zone.slice(1).replace(':', '');
    offsetMinutes = sign * (Number(digits.slice(0, 2)) * 60 + Number(digits.slice(2, 4)));
  }
  return date.getTime() - offsetMinutes * 60_000;
}

// MARK: - CustomStatusClearAfter

/**
 * The classic "Clear after" choices, in menu order. The value is the `duration` tag the
 * server logs alongside the expiry (Swift's `wireTag`).
 */
export enum CustomStatusClearAfter {
  dontClear = '',
  thirtyMinutes = 'thirty_minutes',
  oneHour = 'one_hour',
  fourHours = 'four_hours',
  today = 'today',
  thisWeek = 'this_week',
}

/** Swift's `CaseIterable.allCases`, in menu order. */
export const customStatusClearAfterAllCases: readonly CustomStatusClearAfter[] = [
  CustomStatusClearAfter.dontClear,
  CustomStatusClearAfter.thirtyMinutes,
  CustomStatusClearAfter.oneHour,
  CustomStatusClearAfter.fourHours,
  CustomStatusClearAfter.today,
  CustomStatusClearAfter.thisWeek,
];

// MARK: - CustomStatusDurations
//
// Turns a "clear after" choice into the actual expiry instant, in the USER's calendar — "Today"
// ends when their day does, not when UTC's does.

/** The instant `timeZone`'s wall clock reads 00:00 on the civil date y-m-d (d may overflow). */
function zonedMidnight(year: number, month: number, day: number, timeZone: string | undefined): number {
  const norm = new Date(Date.UTC(year, month - 1, day));
  const target = norm.getTime();
  const wall = (ms: number) => {
    const c = civil(ms, timeZone);
    return Date.UTC(c.year, c.month - 1, c.day, c.hour, c.minute, c.second);
  };
  let guess = target;
  for (let i = 0; i < 3; i++) guess += target - wall(guess);
  return guess;
}

/** `timeZone` stands in for Swift's `calendar` (a Gregorian calendar in that zone). */
export function expiresAt(option: CustomStatusClearAfter, now: number, timeZone?: string): number | null {
  switch (option) {
    case CustomStatusClearAfter.dontClear:
      return null;
    case CustomStatusClearAfter.thirtyMinutes:
      return now + 30 * 60_000;
    case CustomStatusClearAfter.oneHour:
      return now + 60 * 60_000;
    case CustomStatusClearAfter.fourHours:
      return now + 4 * 60 * 60_000;
    case CustomStatusClearAfter.today: {
      // The last instant of the user's own day: midnight tonight, their clock. Added as a
      // calendar day rather than 86_400 seconds so a DST boundary still lands on midnight.
      const c = civil(now, timeZone);
      return zonedMidnight(c.year, c.month, c.day + 1, timeZone);
    }
    case CustomStatusClearAfter.thisWeek: {
      // Mattermost's week ends Sunday night: the coming Sunday's midnight, their clock —
      // and when today IS Sunday, tonight. Weekday 0 = Sunday here (Swift's `.weekday` 1),
      // regardless of any first-day-of-week display preference.
      const c = civil(now, timeZone);
      const daysUntilSunday = (7 - c.weekday) % 7;
      return zonedMidnight(c.year, c.month, c.day + daysUntilSunday + 1, timeZone);
    }
  }
}

/** RFC3339 UTC, the only shape the server accepts. */
export function toISO(ms: number): string {
  return new Date(Math.floor(ms / 1000) * 1000).toISOString().replace(/\.\d{3}Z$/, 'Z');
}

// MARK: - CustomStatusPresets
//
// `emojiName` is what travels on the wire (Mattermost stores the SHORTCODE, not the character);
// `character` is only ever what gets drawn. The Apple client carries no emoji-name table here —
// a status set from the desktop with some other emoji keeps its name and draws
// `fallbackCharacter`.

export interface CustomStatusPreset {
  emojiName: string;
  character: string;
  text: string;
}

export const fallbackCharacter = '🙂';

const knownCharacters: Record<string, string> = {
  palm_tree: '🌴',
  calendar: '📅',
  house: '🏠',
  face_with_thermometer: '🤒',
  fork_and_knife: '🍴',
  speech_balloon: '💬',
};

/** The character for a stored emoji NAME, or null when this client does not know it. */
export function characterForEmojiName(name: string): string | null {
  return Object.prototype.hasOwnProperty.call(knownCharacters, name) ? knownCharacters[name] : null;
}

/** Namespace forms, for `CustomStatusCodec.fromPropsJSON(...)`-style call sites. */
export const CustomStatusCodec = { fromPropsJSON, isActive, parseISO } as const;
export const CustomStatusDurations = { expiresAt, toISO } as const;
export const CustomStatusPresets = { fallbackCharacter, character: characterForEmojiName } as const;
